import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Card, Button } from 'react-bootstrap';
import useFormGenerator from '../common/FormUtils';
import leadContactHistorySchema from './leadContactHistorySchema';
import LeadContactHistoryService from './LeadContactHistoryService';

const LeadContactHistoryModal = ({
  show,
  onHide,
  leadIdentifier,
  contactHistory,
  onSaved
}) => {
  const [validated, setValidated] = useState(false);
  const [leadSources, setLeadSources] = useState([]);

  useEffect(() => {
    // Use the same lead sources from LeadForm
    const sources = [
      { label: 'Website traffic', value: 'Website traffic' },
      { label: 'Email marketing', value: 'Email marketing' },
      { label: 'Content marketing', value: 'Content marketing' },
      { label: 'Social media', value: 'Social media' },
      { label: 'Trade shows and events', value: 'Trade shows and events' },
      { label: 'Partnerships and referrals', value: 'Partnerships and referrals' },
      { label: 'Cold calling', value: 'Cold calling' },
      { label: 'Direct mail', value: 'Direct mail' }
    ];
    setLeadSources(sources);
  }, []);

  const assignValueToJsonSchema = (field, sources) => {
    if (field.name === 'contactType') {
      return {
        ...field,
        options: sources
      };
    }
    return field;
  };

  const mergedJsonSchema = leadContactHistorySchema.map(field =>
    assignValueToJsonSchema(field, leadSources)
  );

  const handleSubmit = async formData => {
    try {
      const dataToSave = {
        ...formData,
        leadIdentifier: leadIdentifier
      };

      let savedData;
      if (contactHistory && contactHistory.identifier) {
        // Update existing
        savedData = await LeadContactHistoryService.updateContactHistory(
          contactHistory.identifier,
          dataToSave
        );
      } else {
        // Create new
        savedData = await LeadContactHistoryService.createContactHistory(dataToSave);
      }

      console.log('Contact History saved:', savedData);
      onSaved(savedData);
      onHide();
    } catch (error) {
      console.error('Error saving contact history:', error);
    }
  };

  // Helper function to extract date from timestamp
  const extractDate = (timestamp) => {
    if (!timestamp) return null;
    // If it's in timestamp format (YYYY-MM-DDTHH:mm:ss), extract just the date part
    if (timestamp.includes('T')) {
      return timestamp.split('T')[0];
    }
    return timestamp;
  };

  // Prepare data for form
  const formData = contactHistory
    ? {
        ...contactHistory,
        contactDate: extractDate(contactHistory.contactDate),
        contactType: contactHistory.contactType
          ? { label: contactHistory.contactType, value: contactHistory.contactType }
          : null
      }
    : undefined;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {contactHistory ? 'Edit Contact History' : 'Add Contact History'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ContactHistoryForm
          data={formData}
          onSubmit={handleSubmit}
          validated={validated}
          setValidated={setValidated}
          schema={mergedJsonSchema}
          onCancel={onHide}
        />
      </Modal.Body>
    </Modal>
  );
};

const ContactHistoryForm = ({
  data,
  onSubmit,
  validated,
  setValidated,
  schema,
  onCancel
}) => {
  const { formFields, handleSubmit } = useFormGenerator(
    schema,
    onSubmit,
    data,
    setValidated
  );

  return (
    <Form noValidate validated={validated} onSubmit={handleSubmit}>
      {formFields}
      <Card.Footer className="d-flex justify-content-end gap-2">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" type="submit">
          Save
        </Button>
      </Card.Footer>
    </Form>
  );
};

LeadContactHistoryModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  leadIdentifier: PropTypes.string.isRequired,
  contactHistory: PropTypes.object,
  onSaved: PropTypes.func.isRequired
};

ContactHistoryForm.propTypes = {
  data: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  validated: PropTypes.bool.isRequired,
  setValidated: PropTypes.func.isRequired,
  schema: PropTypes.array.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default LeadContactHistoryModal;
